<template>
  <div class="px-3 py-2 border-top">
    <b-alert
      :show="!!sendError"
      variant="danger"
      dismissible
      class="mb-2 py-1 px-2 small"
      @dismissed="store.setSendError(null)"
    >
      {{ sendError }}
    </b-alert>

    <template v-if="windowOpen">
      <b-form @submit.prevent="send">
        <b-form-textarea
          v-model="text"
          :placeholder="$t('whatsapp.composer.placeholder')"
          :maxlength="maxLength"
          :disabled="sending"
          rows="2"
          max-rows="6"
          @keydown.enter.exact.prevent="send"
        />
        <div class="d-flex justify-content-between align-items-center mt-1">
          <small class="text-muted">
            {{ $t('whatsapp.composer.window-closes-in', { time: countdown }) }}
            · {{ $t('whatsapp.composer.hint') }}
          </small>
          <b-button
            type="submit"
            variant="primary"
            size="sm"
            :disabled="sending || !text.trim()"
          >
            <b-spinner v-if="sending" small class="mr-1" />
            {{ $t('whatsapp.composer.send') }}
          </b-button>
        </div>
      </b-form>
    </template>

    <div v-else class="small text-muted d-flex align-items-center">
      <span class="flex-grow-1">
        {{ $t('whatsapp.composer.window-closed') }}
      </span>
      <!-- Templates are the one message deliverable outside the window (M6) —
           but only to a linked, consented contact, which the server enforces. -->
      <b-button
        v-if="conversation.contactId"
        size="sm"
        variant="outline-primary"
        class="ml-2 flex-shrink-0"
        @click="$refs.templateModal.open()"
      >
        {{ $t('whatsapp.composer.send-template') }}
      </b-button>
      <span v-else class="ml-2 flex-shrink-0 font-italic">
        {{ $t('whatsapp.composer.link-contact-first') }}
      </span>
    </div>

    <template-send-modal
      v-if="conversation.contactId"
      ref="templateModal"
      :fixed-contact="{
        contactId: conversation.contactId,
        name: conversation.contactName,
      }"
    />
  </div>
</template>

<script>
import { getModule } from 'vuex-module-decorators';
import TemplateSendModal from '@/components/whatsapp/TemplateSendModal.vue';
import { WhatsappModule } from '@/store/whatsapp.module';

/** Mirrors Meta's free-form text cap; the API rejects anything longer anyway. */
const MAX_LENGTH = 4096;

/**
 * The reply box (GID-2697, M3), and the place the 24h customer-service window
 * becomes visible: while open it counts down, and once it closes the input is
 * disabled with the reason spelled out. An admin must never learn about the
 * window from a silent non-delivery minutes after hitting send.
 *
 * Sending is fire-and-acknowledge: the API's 202 means "queued", and the
 * message joins the thread through the WebSocket once gid-crud records it —
 * as a failed bubble if the send dies. So there is no optimistic echo here.
 */
export default {
  name: 'Composer',
  components: { TemplateSendModal },
  props: {
    conversation: { type: Object, required: true },
  },
  data() {
    return {
      text: '',
      // Ticks every 30s so the countdown moves and the composer flips to
      // "closed" when the deadline passes locally — the server-computed
      // `timeWindowOpen` is only as fresh as the last fetch.
      now: Date.now(),
      timer: null,
      maxLength: MAX_LENGTH,
    };
  },
  computed: {
    store() {
      return getModule(WhatsappModule, this.$store);
    },
    sending() {
      return this.store.sending;
    },
    sendError() {
      return this.store.sendError;
    },
    expiresAtMs() {
      return this.conversation.timeWindowExpiresAt
        ? Date.parse(this.conversation.timeWindowExpiresAt)
        : null;
    },
    /**
     * Server verdict AND local clock. `=== true` fails closed like the list
     * badge; the local check closes the composer the moment the deadline
     * passes, without waiting for a refetch.
     */
    windowOpen() {
      if (this.conversation.timeWindowOpen !== true) return false;
      if (this.expiresAtMs === null) return true;
      return this.expiresAtMs > this.now;
    },
    countdown() {
      if (this.expiresAtMs === null) return '';
      const left = Math.max(0, this.expiresAtMs - this.now);
      const hours = Math.floor(left / 3600000);
      const minutes = Math.floor((left % 3600000) / 60000);
      return hours > 0 ? `${hours}h ${minutes}min` : `${minutes}min`;
    },
  },
  mounted() {
    this.timer = setInterval(() => {
      this.now = Date.now();
    }, 30000);
  },
  beforeDestroy() {
    clearInterval(this.timer);
  },
  methods: {
    async send() {
      const text = this.text.trim();
      if (!text || this.sending) return;
      const accepted = await this.store.sendMessage({
        conversationId: this.conversation.id,
        text,
      });
      if (accepted) {
        this.text = '';
      }
      // On rejection the text stays put — the admin should be able to copy or
      // retry it once the reason (shown above) is dealt with.
    },
  },
};
</script>
